import type { EfficiencyReport, EfficiencyWindow } from "@szyos/contracts";
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { api, errorMessage } from "../api";
import { dateTime } from "../format";
import { Icon } from "../icons";

/* Effizienz und Nutzen: Anteil am Wochenlimit, Verbrauch je Ergebnis, Vergleich mit Claude Code. */

const pct = (n: number | null) => (n === null ? "–" : `${Math.round(n * 100).toLocaleString("de-DE")} %`);
const money = (n: number | null) =>
  n === null
    ? "–"
    : `${n.toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} USD`;
const num = (n: number) => n.toLocaleString("de-DE");

const METHOD_TEXT: Record<EfficiencyReport["share"]["method"], string> = {
  gemessen: "gemessen",
  geschätzt: "geschätzt, eher zu hoch",
  richtwert: "Richtwert: Max 20x ≈ 1.643 USD je Woche",
  offen: "noch offen",
};

/** Bericht, jede Minute aktualisiert, damit die Anzeige dem tatsächlichen Stand folgt. */
export function useEfficiency() {
  return useQuery({ queryKey: ["efficiency"], queryFn: api.efficiency, refetchInterval: 60_000 });
}

function Figure({ label, value, sub }: { label: string; value: string; sub: string }) {
  return (
    <div className="efficiency-figure">
      <strong className="efficiency-figure-value">{value}</strong>
      <span className="efficiency-figure-label">{label}</span>
      <small className="muted">{sub}</small>
    </div>
  );
}

/** Ein Satz zum Nutzen: wie teuer szyOS je Zeile ist und was Claude Code dafür geschafft hätte. */
export function verdict(w: EfficiencyWindow): string {
  const c = w.comparison;
  if (c.factor === null || c.claudeCodeLinesForSzyosSpend === null) {
    return "Für den Vergleich je Zeile fehlen noch gemergte Zeilen (ab 50 auf jeder Seite).";
  }
  const factor = c.factor.toLocaleString("de-DE", { maximumFractionDigits: 1 });
  const relation = c.factor >= 1 ? `das ${factor}-fache` : `nur das ${factor}-fache`;
  return `szyOS braucht je geänderter Zeile ${relation} von Claude Code. Mit dem szyOS-Verbrauch hätte Claude Code bei deinem Schnitt etwa ${num(c.claudeCodeLinesForSzyosSpend)} Zeilen geschafft.`;
}

function shareText(r: EfficiencyReport): string {
  const s = r.share;
  if (s.method === "offen") return "Noch keine Meldung des Wochenlimits.";
  return `Konto ${pct(s.accountUtilization)} · ${METHOD_TEXT[s.method]}`;
}

export function EfficiencyPanel({ detailed = false }: { detailed?: boolean }) {
  const q = useEfficiency();
  return (
    <section className="panel usage-panel efficiency-panel" aria-labelledby="efficiencyTitle">
      <div className="panel-head">
        <h2 id="efficiencyTitle">Nutzen und Verbrauch</h2>
        {!detailed && (
          <Link to="/usage" className="link">
            Details <Icon name="arrow" />
          </Link>
        )}
      </div>
      {q.isError && <p className="muted">Bericht nicht geladen: {errorMessage(q.error)}</p>}
      {!q.data && !q.isError && <p className="muted">Lade Bericht …</p>}
      {q.data && <EfficiencyBody report={q.data} detailed={detailed} />}
    </section>
  );
}

function EfficiencyBody({ report: r, detailed }: { report: EfficiencyReport; detailed: boolean }) {
  const w = r.week;
  const s = r.share;
  return (
    <>
      <div className="efficiency-figures">
        <Figure label="Dein Anteil am Wochenlimit" value={pct(s.ownOfLimit)} sub={shareText(r)} />
        <Figure
          label="szyOS je 100 Zeilen"
          value={money(w.comparison.szyosUsdPer100Lines)}
          sub={`Claude Code ${money(w.comparison.claudeCodeUsdPer100Lines)}`}
        />
        <Figure
          label="szyOS diese Woche"
          value={`${num(w.szyos.tasks)} ${w.szyos.tasks === 1 ? "Aufgabe" : "Aufgaben"}`}
          sub={`verifiziert · ${num(w.szyos.merges)} gemergt · ${money(w.szyos.usd)}`}
        />
        <Figure
          label="Claude Code diese Woche"
          value={`${num(w.claudeCode.commits)} Commits`}
          sub={`${num(w.claudeCode.lines)} Zeilen · ${money(w.claudeCode.usd)}`}
        />
      </div>
      <p>{verdict(w)}</p>
      {s.szyosOfOwn !== null && (
        <p className="muted small">
          Dein Verbrauch diese Woche: szyOS {pct(s.szyosOfOwn)}, Claude Code {pct(1 - s.szyosOfOwn)}.
        </p>
      )}
      {r.collectors.length === 0 && (
        <p className="muted small">
          Claude Code meldet noch nicht. Auf dem Rechner einrichten:{" "}
          <code>pwsh -File tools\verbrauch\einrichten.ps1</code>
        </p>
      )}
      {detailed && <EfficiencyDetails report={r} />}
    </>
  );
}

function EfficiencyDetails({ report: r }: { report: EfficiencyReport }) {
  const cols: [string, EfficiencyWindow][] = [
    ["Diese Abo-Woche", r.week],
    ["Letzte 30 Tage", r.last30Days],
  ];
  const rows: [string, (w: EfficiencyWindow) => string][] = [
    ["szyOS: USD-Gegenwert", (w) => money(w.szyos.usd)],
    ["szyOS: Läufe", (w) => num(w.szyos.runs)],
    ["szyOS: verifizierte Aufgaben", (w) => num(w.szyos.tasks)],
    ["szyOS: gemergte Aufträge", (w) => num(w.szyos.merges)],
    ["szyOS: gemergte Zeilen", (w) => num(w.szyos.lines)],
    ["szyOS: je 100 Zeilen", (w) => money(w.comparison.szyosUsdPer100Lines)],
    ["szyOS: je Aufgabe", (w) => money(w.comparison.szyosUsdPerTask)],
    ["Claude Code: USD-Gegenwert", (w) => money(w.claudeCode.usd)],
    ["Claude Code: Antworten", (w) => num(w.claudeCode.messages)],
    ["Claude Code: Commits", (w) => num(w.claudeCode.commits)],
    ["Claude Code: Zeilen", (w) => num(w.claudeCode.lines)],
    ["Claude Code: je 100 Zeilen", (w) => money(w.comparison.claudeCodeUsdPer100Lines)],
  ];
  return (
    <>
      <table className="efficiency-table">
        <caption className="sr-only">Verbrauch und Ergebnisse im Vergleich</caption>
        <thead>
          <tr>
            <th scope="col">Kennzahl</th>
            {cols.map(([label]) => (
              <th key={label} scope="col">
                {label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map(([label, value]) => (
            <tr key={label}>
              <th scope="row">{label}</th>
              {cols.map(([col, w]) => (
                <td key={col} className="mono">
                  {value(w)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      <p className="muted small">Letzte 30 Tage: {verdict(r.last30Days)}</p>
      <h3>Personen diese Woche</h3>
      <ul className="efficiency-persons">
        {r.week.persons.map((p) => (
          <li key={p.person}>
            <strong>{p.person === "owner" ? "Du" : p.person}</strong>: Claude Code {money(p.claudeCodeUsd)}
            {p.person === "owner" ? `, szyOS ${money(p.szyosUsd)}` : ""}
            {p.sources.length > 0 && (
              <span className="muted">
                {" "}
                · {p.sources.join(", ")}
                {p.lastReportAt ? `, zuletzt ${dateTime(p.lastReportAt)}` : ""}
              </span>
            )}
          </li>
        ))}
      </ul>
      <p className="muted small">
        Gemeinsame Einheit ist der USD-Gegenwert zu API-Listenpreisen; im Abo zahlst du ihn nicht, er macht
        szyOS und Claude Code aber vergleichbar. Ergebnis von szyOS sind verifizierte Aufgaben und die Zeilen
        gemergter Aufträge, Ergebnis von Claude Code sind deine Commits, während Claude Code aktiv war. Den
        Anteil am Wochenlimit misst szyOS, sobald alle Personen des Kontos melden; sonst schätzt es ihn aus
        dem Anstieg der Auslastung gegen den gemessenen Verbrauch ({r.share.intervals} Abschnitte), eher zu
        hoch als zu niedrig. Bis dahin gilt ein veröffentlichter Richtwert: eine Woche im Max-Plan 20x
        entspricht etwa 1.643 USD (Messung an 17 Konten, Oktober 2026); Anthropic selbst nennt keine Zahl.
        {r.week.claudeCode.unknownModels.length > 0
          ? ` Ohne Listenpreis, wie Opus 5.5 gerechnet: ${r.week.claudeCode.unknownModels.join(", ")}.`
          : ""}
      </p>
    </>
  );
}

const TRIAL_STATE = {
  open: { icon: "clock", text: "noch offen" },
  met: { icon: "check", text: "erreicht" },
  missed: { icon: "alert", text: "verfehlt" },
} as const;

/** Bewährungsphase (docs/21): die vier messbaren Kriterien mit Ziel und Urteil, laufend aktualisiert. */
export function TrialPanel() {
  const q = useEfficiency();
  if (!q.data) return null;
  const t = q.data.trial;
  const end = new Date(Date.parse(t.to) - 1).toLocaleDateString("de-DE", {
    day: "2-digit",
    month: "2-digit",
  });
  return (
    <section className="panel usage-panel trial-panel" aria-labelledby="trialTitle">
      <div className="panel-head">
        <h2 id="trialTitle">Bewährungsphase</h2>
        <span className="muted">
          bis {end} · noch {t.daysLeft} {t.daysLeft === 1 ? "Tag" : "Tage"}
        </span>
      </div>
      <ul className="trial-list">
        {t.criteria.map((c) => {
          const state = TRIAL_STATE[c.met === null ? "open" : c.met ? "met" : "missed"];
          return (
            <li key={c.key} className={`trial-row is-${c.met === null ? "open" : c.met ? "met" : "missed"}`}>
              <Icon name={state.icon} />
              <span className="trial-label">{c.label}</span>
              <strong>{c.text}</strong>
              <span className="muted small">
                Ziel {c.target} · {state.text}
              </span>
            </li>
          );
        })}
      </ul>
      <p className="muted small">
        Funktionsstopp bis zur Entscheidung. Ob szyOS dir mehr Zeit spart, als es kostet, beurteilst du am
        Ende selbst.
      </p>
    </section>
  );
}
